import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../../data";
import { useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { date, money } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { billingAvailable, billingChange, openBillingPortal, type AboData, type AboRechnung, type RechnungsBank } from "./data";
import { BankKasten, RECHNUNG_ZAHLUNGSZIEL_TAGE, RechnungAbschluss } from "./RechnungAbschluss";
import type { LifecycleStatus } from "./model";

/**
 * Abo & Tarif · „Zahlung auf Rechnung“ (0590). Läuft das Abo auf Rechnung: Bankverbindung, Rechnungen mit Fälligkeit und Link zur
 * Rechnung bei Stripe, „Auf Karte umstellen“ (braucht eine hinterlegte Karte; sonst führt der Knopf ins Kundenportal zum
 * Hinterlegen). Läuft es per Karte und ist die Bankverbindung eingetragen: „Auf Rechnung umstellen“. Demo-Vorschau mit ?vorschau=rechnung.
 */
type Lage = "rechnung" | "karte" | null;

/** Stand einer Rechnung: offen, überfällig, bezahlt, storniert */
export function rechnungStand(r: Pick<AboRechnung, "status" | "due_date">, nowMs: number): { text: string; klasse: string; zeichen: string } {
  if (r.status === "paid") return { text: t("Bezahlt", "Paid"), klasse: "success", zeichen: "✓" };
  if (r.status === "void") return { text: t("Storniert", "Void"), klasse: "", zeichen: "○" };
  if (r.status === "uncollectible") return { text: t("Uneinbringlich", "Uncollectible"), klasse: "error", zeichen: "!" };
  if (r.status === "draft") return { text: t("In Vorbereitung", "Being prepared"), klasse: "", zeichen: "○" };
  if (r.due_date && Date.parse(r.due_date) < nowMs) return { text: t("Überfällig", "Overdue"), klasse: "error", zeichen: "!" };
  return { text: t("Offen", "Open"), klasse: "info", zeichen: "◇" };
}

/** Demo-Vorschau (nur api.mode demo): erfundene Rechnungen und die übliche Test-IBAN */
function demoVorschau(): { bank: RechnungsBank; rechnungen: AboRechnung[] } | null {
  if (api.mode !== "demo" || typeof window === "undefined" || new URLSearchParams(window.location.search).get("vorschau") !== "rechnung") return null;
  const tag = 86_400_000;
  const jetzt = Date.now();
  const r = (n: string, status: AboRechnung["status"], vor: number, faellig: number): AboRechnung => ({
    stripe_invoice_id: `in_demo_${n}`, number: n, status, currency: "EUR", amount_due_cents: 12900, amount_remaining_cents: status === "paid" ? 0 : 12900,
    due_date: new Date(jetzt + faellig * tag).toISOString(), hosted_invoice_url: null, invoice_pdf: null,
    paid_at: status === "paid" ? new Date(jetzt - (vor - 3) * tag).toISOString() : null, stripe_created_at: new Date(jetzt - vor * tag).toISOString(),
  });
  return { bank: { iban: "DE89370400440532013000", bic: "COBADEFFXXX", holder: "Beispiel Empfänger" }, rechnungen: [r("CO-0102", "open", 2, 12), r("CO-0101", "paid", 32, -18)] };
}

export function RechnungPanel({ orgId, data, life }: { orgId: string; data: AboData; life: LifecycleStatus | null; timeZone: string }) {
  const qc = useQueryClient();
  const { busy, error, run } = useAction();
  const [umstellen, setUmstellen] = useState(false);
  const [karteFehlt, setKarteFehlt] = useState(false);
  const [nowMs] = useState(() => Date.now());
  const demo = demoVorschau();
  const sub = data.subscription;
  const bank = demo?.bank ?? data.invoiceBank;
  const rechnungen = demo?.rechnungen ?? data.rechnungen;
  const stripeLaeuft = !!life?.stripe_managed && !life?.paypal_managed && ["active", "trialing", "past_due"].includes(sub?.status ?? "");
  const lage: Lage = demo || (stripeLaeuft && sub?.collection_method === "send_invoice") ? "rechnung"
    : stripeLaeuft && bank && sub?.status !== "past_due" ? "karte" : null;
  if (!lage && !rechnungen.length) return null;
  const neu = async () => {
    await qc.invalidateQueries({ queryKey: ["subscriptions", orgId] });
    await qc.invalidateQueries({ queryKey: ["org_lifecycle", orgId] });
  };
  const aufKarte = () => run("karte", async () => {
    setKarteFehlt(false);
    try {
      await billingChange(api, orgId, { action: "card_switch" });
    } catch (e) {
      if (e instanceof ApiError && e.hint === "billing:card_missing") { setKarteFehlt(true); return; }
      throw e;
    }
    await neu();
    toast(t("Umgestellt: Ab der nächsten Abrechnung bucht Stripe von deiner Karte ab.", "Switched: from the next billing period Stripe charges your card."));
  });
  const offen = rechnungen.filter((r) => r.status === "open");
  return (
    <section className="panel stack tk-rechnung-panel" id="rechnungen" aria-labelledby="rechnungen-titel">
      <h3 id="rechnungen-titel">{t("Zahlung auf Rechnung", "Payment by invoice")}</h3>
      {lage === "rechnung" && (
        <>
          <p>{t(`Dein Abo läuft auf Rechnung. Zu jedem Abrechnungszeitraum kommt eine Rechnung per Mail; du überweist sie innerhalb von ${RECHNUNG_ZAHLUNGSZIEL_TAGE} Tagen mit der Rechnungsnummer als Verwendungszweck.`,
            `Your subscription is paid by invoice. For each billing period you receive an invoice by email; you pay it by bank transfer within ${RECHNUNG_ZAHLUNGSZIEL_TAGE} days with the invoice number as the payment reference.`)}</p>
          {bank ? <BankKasten bank={bank} nummer={offen.length === 1 ? offen[0]!.number : null} /> : <p>{t("Die Bankverbindung steht auf jeder Rechnung.", "The bank details are on every invoice.")}</p>}
        </>
      )}
      {lage === "karte" && !umstellen && (
        <p>{t(`Lieber per Überweisung zahlen? Dann kommt zu jedem Abrechnungszeitraum eine Rechnung mit ${RECHNUNG_ZAHLUNGSZIEL_TAGE} Tagen Zahlungsziel statt der Abbuchung von der Karte.`,
          `Prefer to pay by bank transfer? Then you get an invoice due in ${RECHNUNG_ZAHLUNGSZIEL_TAGE} days for each billing period instead of a card charge.`)}</p>
      )}
      {rechnungen.length > 0 && (
        <ul className="tk-rechnung-liste" aria-label={t("Rechnungen", "Invoices")}>
          {rechnungen.map((r) => {
            const s = rechnungStand(r, nowMs);
            return (
              <li key={r.stripe_invoice_id}>
                <span className="tk-rechnung-nr mono">{r.number ?? t("ohne Nummer", "no number")}</span>
                <span className="num">{money(r.status === "open" ? r.amount_remaining_cents : r.amount_due_cents, r.currency)}</span>
                <span className="tk-rechnung-datum">{r.status === "paid" && r.paid_at ? t(`bezahlt am ${date(r.paid_at)}`, `paid on ${date(r.paid_at)}`)
                  : r.due_date && r.status === "open" ? t(`fällig am ${date(r.due_date)}`, `due on ${date(r.due_date)}`) : r.stripe_created_at ? date(r.stripe_created_at) : ""}</span>
                <span className={`pill ${s.klasse}`.trim()}><span className="pill-zeichen">{s.zeichen}</span> {s.text}</span>
                {(r.hosted_invoice_url || r.invoice_pdf) && (
                  <a href={r.hosted_invoice_url ?? r.invoice_pdf!} target="_blank" rel="noreferrer">{t("Rechnung ansehen", "View invoice")}</a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {lage === "rechnung" && offen.some((r) => r.due_date && Date.parse(r.due_date) < nowMs) && (
        <p className="notice warning" role="status">{t("Eine Rechnung ist überfällig. Bitte überweise den offenen Betrag; bleibt sie länger offen, ist das Konto danach nur noch lesbar, bis die Zahlung eingegangen ist.",
          "An invoice is overdue. Please transfer the open amount; if it stays unpaid longer, the account becomes read-only until the payment has arrived.")}</p>
      )}
      {lage === "rechnung" && !demo && (
        <div className="row">
          <button type="button" disabled={!!busy || !billingAvailable(api)} aria-busy={busy === "karte"} onClick={() => void aufKarte()}>
            {busy === "karte" ? t("Wird umgestellt …", "Switching …") : t("Auf Karte umstellen", "Switch to card")}
          </button>
        </div>
      )}
      {karteFehlt && (
        <div className="notice stack" role="status">
          <span>{t("Dafür brauchst du eine hinterlegte Karte. Hinterlege sie im Kundenportal und stelle danach hier um.", "You need a card on file for this. Add it in the customer portal, then switch here.")}</span>
          <div className="row">
            <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "portal"}
              onClick={() => void run("portal", async () => { window.location.assign(await openBillingPortal(api, orgId, "payment_method_update")); })}>
              {busy === "portal" ? t("Kundenportal wird geöffnet …", "Opening customer portal …") : t("Karte hinterlegen", "Add card")}
            </button>
          </div>
        </div>
      )}
      {lage === "karte" && bank && (umstellen
        ? <RechnungAbschluss orgId={orgId} bank={bank} modus="umstellen" onAbbrechen={() => setUmstellen(false)}
            onFertig={async () => { await neu(); setUmstellen(false); toast(t("Umgestellt: Ab der nächsten Abrechnung kommt eine Rechnung per Mail.", "Switched: from the next billing period you get an invoice by email.")); }} />
        : <div className="row"><button type="button" onClick={() => setUmstellen(true)}>{t("Auf Rechnung umstellen", "Switch to invoice")}</button></div>)}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}
